import { useState, useEffect } from "react";
import {
  getMyRestaurant,
  toggleRestaurantStatus,
  getRestaurantFoods,
  getRestaurantOrders,
  updateOrderStatus,
} from "../services/restaurantService";

const STATUS_COLORS = {
  PENDING:        "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  OUT_FOR_DELIVERY: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  DELIVERED:      "bg-green-500/10 text-green-400 border-green-500/30",
  COMPLETED:      "bg-green-500/10 text-green-400 border-green-500/30",
};

const NEXT_STATUS = {
  PENDING: "OUT_FOR_DELIVERY",
  OUT_FOR_DELIVERY: "DELIVERED",
  DELIVERED: "COMPLETED",
};

export default function DashboardPage() {
  const [restaurant, setRestaurant] = useState(null);
  const [foods, setFoods]           = useState([]);
  const [orders, setOrders]         = useState([]);
  const [activeTab, setActiveTab]   = useState("overview");
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href = "/"; return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const rest = await getMyRestaurant();
      setRestaurant(rest);
      const [f, o] = await Promise.all([
        getRestaurantFoods(rest.id),
        getRestaurantOrders(rest.id),
      ]);
      setFoods(f);
      setOrders([...o].reverse());

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    try {
      const updated = await toggleRestaurantStatus(restaurant.id);
      setRestaurant(updated);
    } catch (e) { setError(e.message); }
  };

  const handleOrderStatus = async (orderId, status) => {
    try {
      const updated = await updateOrderStatus(orderId, status);
      setOrders(orders.map(o => o.id === orderId ? updated : o));
    } catch (e) { setError(e.message); }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const totalRevenue = orders
    .filter(o => o.orderStatus === "COMPLETED" || o.orderStatus === "DELIVERED")
    .reduce((sum, o) => sum + (o.totalPrice || 0), 0);

  const pendingOrders = orders.filter(o => o.orderStatus === "PENDING").length;

  if (loading) return (
    <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-zinc-500 text-sm">Loading dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">

      {/* Navbar */}
      <nav className="bg-[#1a1a1a] border-b border-zinc-800 px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🍽️</span>
          <span className="text-xl font-black">Sav<span className="text-orange-500">ora</span></span>
          <span className="text-zinc-600 mx-2">|</span>
          <span className="text-zinc-400 text-sm">{restaurant?.name}</span>
        </div>
        <div className="flex items-center gap-4">
          {/* Open/Close Toggle */}
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${
              restaurant?.open
                ? "bg-green-500/10 text-green-400 border-green-500/30"
                : "bg-red-500/10 text-red-400 border-red-500/30"
            }`}>
              {restaurant?.open ? "● OPEN" : "● CLOSED"}
            </span>
            <button
              onClick={handleToggle}
              className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-3 py-1.5 rounded-lg transition-colors"
            >
              Toggle
            </button>
          </div>
          <button onClick={handleLogout}
            className="text-xs text-zinc-500 hover:text-red-400 transition-colors">
            Logout →
          </button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-6">
            <p className="text-red-400 text-sm">⚠️ {error}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Orders", value: orders.length, icon: "📦", color: "text-blue-400" },
            { label: "Pending", value: pendingOrders, icon: "⏳", color: "text-yellow-400" },
            { label: "Menu Items", value: foods.length, icon: "🍱", color: "text-orange-400" },
            { label: "Revenue", value: `₹${totalRevenue.toLocaleString()}`, icon: "💰", color: "text-green-400" },
          ].map((stat) => (
            <div key={stat.label} className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-5">
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className={`text-2xl font-black ${stat.color}`}>{stat.value}</div>
              <div className="text-zinc-500 text-xs mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-zinc-900 rounded-xl p-1 mb-6 w-fit">
          {["overview", "orders", "menu"].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all capitalize ${
                activeTab === tab
                  ? "bg-orange-500 text-white"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}>
              {tab === "overview" ? "📊 Overview" : tab === "orders" ? "📦 Orders" : "🍱 Menu"}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Restaurant Info */}
            <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-6">
              <h2 className="text-sm text-zinc-400 uppercase tracking-wider mb-4">Restaurant Info</h2>
              {restaurant?.images?.[0] && (
                <img src={restaurant.images[0]} alt={restaurant.name}
                  className="w-full h-40 object-cover rounded-xl mb-4" />
              )}
              <h3 className="text-xl font-bold text-white mb-1">{restaurant?.name}</h3>
              <p className="text-zinc-500 text-sm mb-3">{restaurant?.description}</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Cuisine</span>
                  <span className="text-zinc-300">{restaurant?.cuisineType}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Hours</span>
                  <span className="text-zinc-300">{restaurant?.openingHours}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">City</span>
                  <span className="text-zinc-300">{restaurant?.address?.city}</span>
                </div>
              </div>
              <button onClick={() => window.location.href = "/restaurant"}
                className="mt-4 w-full text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 py-2.5 rounded-xl transition-colors">
                Manage Restaurant →
              </button>
            </div>

            {/* Recent Orders */}
            <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-6">
              <h2 className="text-sm text-zinc-400 uppercase tracking-wider mb-4">Recent Orders</h2>
              {orders.length === 0 ? (
                <div className="text-center py-8 text-zinc-600">
                  <div className="text-4xl mb-2">📭</div>
                  <p className="text-sm">Abhi koi order nahi</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.slice(0, 5).map(order => (
                    <div key={order.id} className="flex items-center justify-between bg-zinc-900 rounded-xl px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-white">Order #{order.id}</p>
                        <p className="text-xs text-zinc-500">₹{order.totalPrice}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_COLORS[order.orderStatus] || "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>
                        {order.orderStatus}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-6">
            <h2 className="text-sm text-zinc-400 uppercase tracking-wider mb-4">All Orders</h2>
            {orders.length === 0 ? (
              <div className="text-center py-12 text-zinc-600">
                <div className="text-5xl mb-3">📭</div>
                <p>Koi order nahi abhi tak</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map(order => (
                  <div key={order.id} className="bg-zinc-900 rounded-xl px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">Order #{order.id}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {order.items?.length || 0} items · ₹{order.totalPrice}
                      </p>
                      <p className="text-xs text-zinc-600 mt-0.5">
                        {order.deliveryAddress?.city}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_COLORS[order.orderStatus] || "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>
                        {order.orderStatus}
                      </span>
                      {NEXT_STATUS[order.orderStatus] && (
                        <button
                          onClick={() => handleOrderStatus(order.id, NEXT_STATUS[order.orderStatus])}
                          className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg transition-colors"
                        >
                          → {NEXT_STATUS[order.orderStatus]}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Menu Tab */}
        {activeTab === "menu" && (
          <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm text-zinc-400 uppercase tracking-wider">Menu Items</h2>
              <button onClick={() => window.location.href = "/food"}
                className="text-xs bg-orange-500 hover:bg-orange-600 text-white font-bold px-3 py-1.5 rounded-lg transition-colors">
                Manage Menu →
              </button>
            </div>
            {foods.length === 0 ? (
              <div className="text-center py-12 text-zinc-600">
                <div className="text-5xl mb-3">🍽️</div>
                <p>Koi food item nahi abhi tak</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {foods.map(food => (
                  <div key={food.id} className="bg-zinc-900 rounded-xl overflow-hidden">
                    {food.images?.[0] && (
                      <img src={food.images[0]} alt={food.name}
                        className="w-full h-36 object-cover" />
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-semibold text-white text-sm">{food.name}</h3>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ml-2 shrink-0 ${
                          food.vegetarian
                            ? "bg-green-500/10 text-green-400"
                            : "bg-red-500/10 text-red-400"
                        }`}>
                          {food.vegetarian ? "🟢 Veg" : "🔴 Non-veg"}
                        </span>
                      </div>
                      <p className="text-zinc-500 text-xs mb-2 line-clamp-2">{food.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-orange-400 font-bold">₹{food.price}</span>
                        <span className={`text-xs ${food.available ? "text-green-400" : "text-zinc-600"}`}>
                          {food.available ? "● Available" : "● Unavailable"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}